import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Subscription } from '../../types';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { usePageTitle } from '../../hooks/usePageTitle';
import { formatDate } from '../../utils/dateUtils';
import {
  RenewalStatus,
  daysUntilExpiration,
  getRenewalLabel,
  getRenewalStatus,
  isExpiringSoon,
} from '../../utils/renewalStatus';

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  payment_complete_pending_account: 'Pending Onboarding',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

type RenewalFilter =
  | 'all'
  | 'expiring-soon'
  | 'expired'
  | '30-day'
  | '60-day'
  | '90-day'
  | 'healthy';

const RENEWAL_FILTER_LABELS: Record<RenewalFilter, string> = {
  all: 'All',
  'expiring-soon': 'Expiring Soon (≤ 90 days)',
  expired: 'Expired',
  '30-day': 'Within 30 days',
  '60-day': 'Within 60 days',
  '90-day': 'Within 90 days',
  healthy: 'Healthy',
};

const ManageSubscriptions: React.FC = () => {
  usePageTitle('Manage Subscriptions');
  const [searchParams] = useSearchParams();
  const renewalParam = searchParams.get('renewal') as RenewalFilter | null;
  const statusParam = searchParams.get('status');
  // If a renewal filter was passed without an explicit status, default to 'all'
  // so the two filters don't collide (non-active subs have renewalStatus === 'n/a').
  const initialStatus =
    statusParam || (renewalParam ? 'all' : 'payment_complete_pending_account');
  const initialRenewal: RenewalFilter = renewalParam || 'all';
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [renewalFilter, setRenewalFilter] = useState<RenewalFilter>(initialRenewal);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const authFetch = useAuthFetch();

  const fetchData = useCallback(async () => {
    try {
      const res = await authFetch('/api/subscriptions');
      if (res.ok) setSubscriptions(await res.json());
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (id: string) => {
    if (!window.confirm('Approve this subscription? The customer will receive a welcome email and approval notification.')) return;
    setActionLoading(id);
    try {
      const res = await authFetch(`/api/subscriptions/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        await fetchData();
      } else {
        const err = await res.json();
        alert(`Failed to approve: ${err.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error approving subscription:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await authFetch(`/api/subscriptions/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason || undefined }),
      });
      if (res.ok) {
        setRejectingId(null);
        setRejectReason('');
        await fetchData();
      } else {
        const err = await res.json();
        alert(`Failed to reject: ${err.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error rejecting subscription:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm('Cancel this subscription? This action cannot be undone.')) return;
    setActionLoading(id);
    try {
      const res = await authFetch(`/api/subscriptions/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        await fetchData();
      } else {
        const err = await res.json();
        alert(`Failed to cancel: ${err.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleTogglePaid = async (id: string, nextPaid: 0 | 1) => {
    setActionLoading(id);
    try {
      const res = await authFetch(`/api/subscriptions/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paid: nextPaid }),
      });
      if (res.ok) {
        await fetchData();
      } else {
        const err = await res.json();
        alert(`Failed to update paid status: ${err.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error updating paid status:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveNotes = async (id: string) => {
    setActionLoading(id);
    try {
      const res = await authFetch(`/api/subscriptions/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notesDraft }),
      });
      if (res.ok) {
        setEditingNotesId(null);
        setNotesDraft('');
        await fetchData();
      } else {
        const err = await res.json();
        alert(`Failed to save notes: ${err.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error saving notes:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const now = new Date();
  const withRenewal = subscriptions.map((s) => ({
    sub: s,
    renewalStatus: getRenewalStatus(s, now) as RenewalStatus,
    daysLeft: daysUntilExpiration(s, now),
  }));

  const filtered = withRenewal.filter(({ sub, renewalStatus }) => {
    const statusMatch = statusFilter === 'all' || sub.status === statusFilter;
    const renewalMatch =
      renewalFilter === 'all' ||
      (renewalFilter === 'expiring-soon' && isExpiringSoon(renewalStatus)) ||
      renewalStatus === renewalFilter;
    const searchMatch =
      searchQuery === '' ||
      sub.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.customerEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.companyName.toLowerCase().includes(searchQuery.toLowerCase());
    return statusMatch && renewalMatch && searchMatch;
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading subscriptions...</p>
      </div>
    );
  }

  const pendingCount = subscriptions.filter(
    (s) => s.status === 'payment_complete_pending_account'
  ).length;

  return (
    <div className="admin-manage-page">
      <section className="dashboard-header">
        <div className="section-container">
          <h1 className="page-title">Manage Subscriptions</h1>
          {pendingCount > 0 && (
            <p className="page-subtitle" style={{ color: 'var(--yellow)' }}>
              {pendingCount} subscription(s) pending approval
            </p>
          )}
        </div>
      </section>

      <section className="section">
        <div className="section-container">
          <div className="filters-bar">
            <div className="filter-group">
              <label className="filter-label">Status</label>
              <select
                className="filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="payment_complete_pending_account">Pending Onboarding</option>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Renewal</label>
              <select
                className="filter-select"
                value={renewalFilter}
                onChange={(e) => setRenewalFilter(e.target.value as RenewalFilter)}
              >
                {(Object.keys(RENEWAL_FILTER_LABELS) as RenewalFilter[]).map((key) => (
                  <option key={key} value={key}>
                    {RENEWAL_FILTER_LABELS[key]}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group filter-search">
              <label className="filter-label">Search</label>
              <input
                type="text"
                className="filter-input"
                placeholder="Search by name, email, or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <p className="filter-results">
            {filtered.length} subscription{filtered.length !== 1 ? 's' : ''}
          </p>

          <table className="admin-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Email</th>
                <th>Company</th>
                <th>Tier</th>
                <th>Payment</th>
                <th>Reference</th>
                <th>Purchased</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Renewal</th>
                <th>Paid</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(({ sub, renewalStatus, daysLeft }) => (
                <tr key={sub.id}>
                  <td>{sub.customerName}</td>
                  <td>{sub.customerEmail}</td>
                  <td>{sub.companyName || '-'}</td>
                  <td>{sub.tier}</td>
                  <td>{sub.paymentMethod.replace(/_/g, ' ')}</td>
                  <td title={sub.paymentReference}>
                    {sub.paymentReference.length > 20
                      ? sub.paymentReference.substring(0, 20) + '...'
                      : sub.paymentReference}
                  </td>
                  <td>{formatDate(sub.purchaseDate)}</td>
                  <td>
                    {formatDate(sub.expirationDate)}
                    {sub.status === 'active' && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                        {daysLeft < 0 ? `${Math.abs(daysLeft)} days overdue` : `${daysLeft} days left`}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge status-${sub.status}`}>
                      {STATUS_LABELS[sub.status] || sub.status}
                    </span>
                  </td>
                  <td>
                    {renewalStatus === 'n/a' ? (
                      <span style={{ color: 'var(--gray-400)' }}>—</span>
                    ) : (
                      <span className={`status-badge renewal-badge-${renewalStatus}`}>
                        {getRenewalLabel(renewalStatus)}
                      </span>
                    )}
                  </td>
                  <td className="td-paid" style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      aria-label={`Mark ${sub.customerName} as paid`}
                      checked={sub.paid === 1}
                      disabled={actionLoading === sub.id}
                      onChange={(e) => handleTogglePaid(sub.id, e.target.checked ? 1 : 0)}
                    />
                  </td>
                  <td className="td-notes" style={{ maxWidth: '200px' }}>
                    {editingNotesId === sub.id ? (
                      <div className="notes-edit-form">
                        <textarea
                          className="filter-input"
                          rows={3}
                          value={notesDraft}
                          onChange={(e) => setNotesDraft(e.target.value)}
                          style={{ width: '100%', fontSize: '0.85rem', resize: 'vertical' }}
                          autoFocus
                        />
                        <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                          <button
                            className="btn btn-text"
                            onClick={() => handleSaveNotes(sub.id)}
                            disabled={actionLoading === sub.id}
                          >
                            {actionLoading === sub.id ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            className="btn btn-text"
                            onClick={() => { setEditingNotesId(null); setNotesDraft(''); }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div title={sub.notes || ''}>
                        <span style={{ fontSize: '0.85rem', color: sub.notes ? 'inherit' : '#888', whiteSpace: 'pre-wrap' }}>
                          {sub.notes
                            ? (sub.notes.length > 60 ? sub.notes.substring(0, 60) + '…' : sub.notes)
                            : '(none)'}
                        </span>
                        {' '}
                        <button
                          className="btn btn-text"
                          style={{ fontSize: '0.8rem', padding: '0 4px' }}
                          onClick={() => { setEditingNotesId(sub.id); setNotesDraft(sub.notes || ''); }}
                        >
                          edit
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="td-actions">
                    {rejectingId === sub.id ? (
                      <div className="reject-form">
                        <input
                          type="text"
                          className="filter-input"
                          placeholder="Reason (optional)"
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          style={{ marginBottom: '4px', fontSize: '0.85rem' }}
                        />
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            className="btn btn-text btn-danger"
                            onClick={() => handleReject(sub.id)}
                            disabled={actionLoading === sub.id}
                          >
                            {actionLoading === sub.id ? 'Rejecting...' : 'Confirm Reject'}
                          </button>
                          <button
                            className="btn btn-text"
                            onClick={() => { setRejectingId(null); setRejectReason(''); }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {sub.status === 'payment_complete_pending_account' && (
                          <>
                            <button
                              className="btn btn-text"
                              style={{ color: 'var(--green, #28a745)' }}
                              onClick={() => handleApprove(sub.id)}
                              disabled={actionLoading === sub.id}
                            >
                              {actionLoading === sub.id ? 'Approving...' : 'Approve'}
                            </button>
                            <button
                              className="btn btn-text btn-danger"
                              onClick={() => setRejectingId(sub.id)}
                              disabled={actionLoading === sub.id}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {sub.status === 'active' && (
                          <button
                            className="btn btn-text btn-danger"
                            onClick={() => handleCancel(sub.id)}
                            disabled={actionLoading === sub.id}
                          >
                            {actionLoading === sub.id ? 'Cancelling...' : 'Cancel'}
                          </button>
                        )}
                        {sub.status === 'cancelled' && sub.rejectionReason && (
                          <span title={sub.rejectionReason} style={{ fontSize: '0.8rem', color: '#888' }}>
                            Reason: {sub.rejectionReason}
                          </span>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="empty-text">No subscriptions found.</p>}
        </div>
      </section>
    </div>
  );
};

export default ManageSubscriptions;
